import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import SliderField from '@/components/Indicators/SliderField'
import TelegramNotificationsSection from '@/components/Indicators/TelegramNotificationsSection'
import { SL_MODES, LEVERAGE_RANGE, SL_PCT_RANGE, SL_ATR_RANGE, TP_ATR_RANGE, TP1_WEIGHT_RANGE, TIME_STOP_RANGE, resetToDefaults } from './constants.js'

function ToggleRow({ label, value, onChange }) {
  return (
    <label className="flex items-center justify-between cursor-pointer py-0.5">
      <span className="text-xs text-[var(--txt-dim,#B9BDB4)]">{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="w-3.5 h-3.5 rounded border-[var(--hair,#B08D57)] text-[#0B3D2E] focus:ring-[#B08D57] focus:ring-1"
      />
    </label>
  )
}

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

const SL_MODE_LABELS = {
  liquidation: 'Sin SL (liquidación)',
  pct: '% de precio',
  atr: '×ATR',
}

export default function RangoQuantumPanel({ config, onChange, onClose, chartSymbol, chartTimeframe }) {
  const cfg = config || {}

  const update = (key, value) => onChange({ ...cfg, [key]: value })

  const handleReset = () => onChange(resetToDefaults(cfg))

  return (
    <IndicatorConfigModal title="Configuración Rango Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <div className="text-[10px] px-1 pb-1" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
          Defaults = config de serie del estudio (4H, 5x, TP 5/6×ATR 60/40, sin SL técnico). Solo 4H está
          validado — el motor de señales de backend evalúa 4H únicamente.
        </div>

        <Section title="Motor" sectionKey="engine" config={cfg} onChange={onChange}>
          <SliderField
            label="Apalancamiento de referencia"
            value={cfg.leverage ?? 5}
            min={LEVERAGE_RANGE.min}
            max={LEVERAGE_RANGE.max}
            step={1}
            unit="x"
            onChange={(v) => update('leverage', v)}
          />

          <div className="space-y-1">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Modo de SL</span>
            <div className="grid grid-cols-3 gap-1 mt-1">
              {SL_MODES.map((mode) => (
                <button
                  key={mode}
                  onClick={() => update('slMode', mode)}
                  className={`px-1 py-1.5 text-[10px] font-medium rounded border transition-colors ${
                    cfg.slMode === mode
                      ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                      : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {SL_MODE_LABELS[mode]}
                </button>
              ))}
            </div>
          </div>

          {cfg.slMode === 'pct' && (
            <SliderField
              label="Distancia de SL"
              value={cfg.slPct ?? 8}
              min={SL_PCT_RANGE.min}
              max={SL_PCT_RANGE.max}
              step={1}
              unit="%"
              onChange={(v) => update('slPct', v)}
            />
          )}
          {cfg.slMode === 'atr' && (
            <SliderField
              label="Distancia de SL"
              value={cfg.slAtr ?? 3}
              min={SL_ATR_RANGE.min}
              max={SL_ATR_RANGE.max}
              step={0.5}
              unit="×ATR"
              onChange={(v) => update('slAtr', v)}
            />
          )}

          <SliderField
            label="TP1 (distancia)"
            value={cfg.tp1DistanceAtr ?? 5}
            min={TP_ATR_RANGE.min}
            max={TP_ATR_RANGE.max}
            step={0.5}
            unit="×ATR"
            onChange={(v) => update('tp1DistanceAtr', v)}
          />
          <SliderField
            label="TP2 (distancia)"
            value={cfg.tp2DistanceAtr ?? 6}
            min={TP_ATR_RANGE.min}
            max={TP_ATR_RANGE.max}
            step={0.5}
            unit="×ATR"
            onChange={(v) => update('tp2DistanceAtr', v)}
          />
          <SliderField
            label="Reparto en TP1"
            value={cfg.tp1WeightPct ?? 60}
            min={TP1_WEIGHT_RANGE.min}
            max={TP1_WEIGHT_RANGE.max}
            step={5}
            unit="%"
            onChange={(v) => update('tp1WeightPct', v)}
          />
          <SliderField
            label="Time-stop"
            value={cfg.timeStopDays ?? 15}
            min={TIME_STOP_RANGE.min}
            max={TIME_STOP_RANGE.max}
            step={1}
            unit="d"
            formatValue={(v) => (v === 0 ? 'Desactivado' : `${v}d`)}
            onChange={(v) => update('timeStopDays', v)}
          />
        </Section>

        {/* WP H-G: los filtros que aplican los bots (config de serie
            congelada). Activos por defecto — el chart pinta lo que el bot
            operaría. Desactivarlos pinta señales que ningún bot opera; si el
            usuario tiene un bot vivo en esta celda, el badge lo avisa. */}
        <Section title="Filtros de producción" sectionKey="filters" config={cfg} onChange={onChange}>
          <ToggleRow
            label="RSI estrecho (55-70 / 30-45)"
            value={(cfg.filters ?? ['rsi_narrow', 'atr_min_median']).includes('rsi_narrow')}
            onChange={(v) => {
              const cur = cfg.filters ?? ['rsi_narrow', 'atr_min_median']
              update('filters', v ? [...new Set([...cur, 'rsi_narrow'])] : cur.filter((f) => f !== 'rsi_narrow'))
            }}
          />
          <ToggleRow
            label="ATR% sobre mediana (2000 velas)"
            value={(cfg.filters ?? ['rsi_narrow', 'atr_min_median']).includes('atr_min_median')}
            onChange={(v) => {
              const cur = cfg.filters ?? ['rsi_narrow', 'atr_min_median']
              update('filters', v ? [...new Set([...cur, 'atr_min_median'])] : cur.filter((f) => f !== 'atr_min_median'))
            }}
          />
          <div className="text-[9px] px-0.5" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
            Son los filtros con los que operan los bots. Si los desactivas, el chart pinta
            señales que tu bot no operaría.
          </div>
        </Section>

        <Section title="Visuales" sectionKey="visual" config={cfg} onChange={onChange}>
          <ToggleRow label="Etiquetas de señal (➹/➷)" value={cfg.showSignalLabels ?? true} onChange={(v) => update('showSignalLabels', v)} />
          <ToggleRow label="Niveles TP1/TP2/SL de la señal activa" value={cfg.showLevels ?? true} onChange={(v) => update('showLevels', v)} />
          <ToggleRow label="Tabla de últimas señales" value={cfg.showLastSignals ?? true} onChange={(v) => update('showLastSignals', v)} />
        </Section>

        <Section title="Notificaciones" sectionKey="notifications" config={cfg} onChange={onChange}>
          <div className="space-y-2">
            {/* WP-3 fase B: fuera los toggles Nueva señal/TP1/TP2/Liquidación
                (nadie los leía). Rango Quantum sigue fuera del motor de
                alertas del chart; su aviso real es la suscripción Telegram de
                abajo (task rango_quantum_signal_tasks, 4H), que envía señal,
                TP1, TP2 y liquidación juntos, sin selección por tipo. */}
            <p className="text-[10px] leading-snug" style={{ color: 'var(--ink-soft, #4A5A52)' }} data-testid="rango-notifications-note">
              Rango Quantum avisa por Telegram: al suscribirte a un activo (motor 4H) recibes la señal nueva, TP1, TP2 y la liquidación. No se puede elegir solo una parte.
            </p>
            <TelegramNotificationsSection
              indicatorId="rango_quantum"
              symbol={chartSymbol}
              timeframe={chartTimeframe}
              fixedTimeframe="4h"
            />
          </div>
        </Section>

        <button
          onClick={handleReset}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
        >
          <RotateCcw size={12} />
          Restablecer valores Quantum
        </button>
      </div>
    </IndicatorConfigModal>
  )
}
